// Services
import { reportColumns } from "../../../Constants/reportColumns";
import { reportFields } from "../../../Constants/reportFields";
import { getLocationCoordinates } from "../../../Services/locations.service";
import {
  toDateString,
  toDateTime,
  toDistanceFormat,
  toSpeedFormat,
} from "../../../Services/string.service";

import { useEffect, useRef, useState } from "react";
import "./styles.css";

import { useTranslation } from "react-i18next";
import EmptyData from "../../../components/EmptyData";
import { paginationService } from "../../../Services/pagination.service";

import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Page,
  Filter,
  Sort,
} from "@syncfusion/ej2-react-grids";

const NAO_IDENTIFICADO = "NAO IDENTIFICADO";

// Larguras fixas por campo (px) — colunas sem entrada aqui ficam flexíveis
// e absorvem o espaço restante (ex: Localização em /speedcontrol).
const columnWidthsByGrid = {
  gridSpeedControl: {
    licensePlate: 110,
    generatedAt: 140,
    speed: 90,
  },
  gridPeriodVisits: {
    licensePlate: 120,
    startDate: 160,
    duration: 120,
    eventType: 140,
    location: 450,
  },
  gridVehiclePositions: {
    generationDate: 140,
    statusDescription: 80,
    ignition: 90,
    event: 100,
    speed: 110,
    location: 445,
    mainBattery: 110,
    backupBattery: 125,
  },
};

const columnAlignmentsByGrid = {
  gridVehiclePositions: {
    statusDescription: "Center",
    ignition: "Center",
  },
};

function MaybeUnidentified({ value }) {
  if (typeof value === "string" && value.toUpperCase() === NAO_IDENTIFICADO) {
    return (
      <span style={{ color: "#ef4444", fontWeight: 700 }}>
        {value}
      </span>
    );
  }
  return <>{value}</>;
}

// FIX (paliativo): remove linhas 100% idênticas do array antes de renderizar.
// Linhas duplicadas observadas em Controle de Velocidade parecem vir já
// duplicadas da API (`high-speed-summaries`) — este dedup evita que o
// usuário veja a duplicidade na tela enquanto o backend é investigado
// junto ao Reginaldo. Não deve ser usado como substituto da correção real
// no backend, já que se dois eventos DIFERENTES tiverem exatamente os
// mesmos campos (raro, mas possível), um deles seria descartado aqui.
const dedupeItems = (items) => {
  if (!Array.isArray(items)) return [];
  const seen = new Set();
  return items.filter((item) => {
    if (!item) return false;
    const signature = JSON.stringify(item);
    if (seen.has(signature)) return false;
    seen.add(signature);
    return true;
  });
};

// Altura/paginação dinâmicas (mesmo padrão de VehiclesNickame.jsx).
const ROW_HEIGHT = 30; // precisa bater com rowHeight do GridComponent
const HEADER_PAGER_BUDGET = 100;
const BOTTOM_MARGIN = 30;
const MIN_PAGE = 5;
const MAX_PAGE = 100;

const isTemporalField = (field) => {
  const normalizedField = `${field || ""}`.toLowerCase();
  return normalizedField.includes("date") || normalizedField.includes("generatedat");
};

const parseTemporalValue = (value) => {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value !== "string" || !value.trim() || value.includes("0001") || value.includes("1900")) return null;

  const brazilianDate = value.match(/^(\d{2})\/(\d{2})\/(\d{2,4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/);
  if (brazilianDate) {
    const [, day, month, year, hours = "00", minutes = "00", seconds = "00"] = brazilianDate;
    const fullYear = year.length === 2 ? `20${year}` : year;
    const parsedDate = new Date(Number(fullYear), Number(month) - 1, Number(day), Number(hours), Number(minutes), Number(seconds));
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const normalizeTemporalData = (items, fields) => items.map((item) => {
  const normalizedItem = { ...item };
  fields.filter(isTemporalField).forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(normalizedItem, field)) {
      normalizedItem[field] = parseTemporalValue(normalizedItem[field]);
    }
  });
  return normalizedItem;
});

export default function GridReport(props) {
  const { t } = useTranslation();
  const [pageSize, setPageSize] = useState(10);
  const gridAnchorRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        props.gridData = null;
      } catch (error) {
        console.log(error);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.page]);

  const fields = reportFields[props.pageGrid];
  const columns = reportColumns(t)[props.pageGrid];
  const isGeneratedAtField = (field) => `${field || ""}`.toLowerCase() === "generatedat";
  const isSummaryVehiclesGrid = props.pageGrid === "gridSummaryVehicles";
  const isPlateTypeField = (field) => `${field || ""}`.toLowerCase().includes("platetype");
  const plateTypeField = fields.find((field) => isPlateTypeField(field));

  const visibleFieldColumnPairs = fields
    .map((field, index) => ({ field, column: columns[index] }))
    .filter(({ field }) => !(isSummaryVehiclesGrid && isPlateTypeField(field)));

  // Retorna só o CONTEÚDO da célula (sem <td>/key) — o Syncfusion GridComponent
  // já cuida do wrapper via ColumnDirective/template.
  const renderCellContent = (item, field) => {
    const value =
      field === "licensePlate"
        ? item[field] ?? item.LicensePlate ?? item.plate ?? item.vehiclePlate ?? ""
        : item[field];
    if (!field) return null;

    if (field.toLowerCase().includes("plate")) {
      const plateType = plateTypeField ? item[plateTypeField] : (item.licensePlateType || item.plateType);
      const vehicleTooltip = item.PlateTooltip || item.plateTooltip || plateType || item.vehicleDescription || item.vehicleName || item.vehicleId || value;

      if (isSummaryVehiclesGrid) {
        return plateType ? (
          <span title={plateType} style={{ cursor: "help", borderBottom: "1px dashed var(--text-secondary,#aaa)" }}>
            {value}
          </span>
        ) : (
          <span>{value}</span>
        );
      }

      return <span title={vehicleTooltip} style={{ cursor: "help" }}>{value}</span>;
    } else if (
      field.toLowerCase() == "end" ||
      field.toLowerCase() == "start" ||
      field.toLowerCase().includes("date") ||
      isGeneratedAtField(field)
    ) {
      return value
        ? props.pageGrid == "gridVehicleActivities"
          ? toDateString(value)
          : toDateTime(value)
        : "";
    } else if (field.toLowerCase() == "ignition") {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            cursor: "help",
          }}
        >
          {item.ignition === "Ligado" ? (
            <div title="Ligado">
              <img
                src="/images/icons/opened.png"
                alt="Ligado"
                style={{ width: "24px", marginLeft: "0px" }}
              />
            </div>
          ) : item.ignition === "Desligado" ? (
            <div title="Desligado">
              <img
                src="/images/icons/closed.png"
                alt="Desligado"
                style={{ width: "24px", marginLeft: "0px" }}
              />
            </div>
          ) : (
            <div style={{ cursor: "default" }}>{item.ignition}</div>
          )}
        </div>
      );
    } else if (
      field.toLowerCase().includes("speed") &&
      !field.toLowerCase().includes("count")
    ) {
      const speedColor =
        item.speedStatus == "3" || item.speedStatus == "4"
          ? "#FFA500"
          : item.speedStatus != "0"
          ? "#FF0000"
          : "inherit";

      const speedLabel = (
        <span style={{ color: speedColor }}>
          {value ? toSpeedFormat(value) : "0.00 km/h"}
        </span>
      );

      if (item.speedStatusDescription && item.speedStatusDescription !== "") {
        return (
          <span title={item.speedStatusDescription} style={{ cursor: "help" }}>{speedLabel}</span>
        );
      }
      return speedLabel;
    } else if (
      field.toLowerCase().includes("distance") ||
      field.toLowerCase().includes("km")
    ) {
      return value ? toDistanceFormat(value) : "";
    } else if (field.toLowerCase().includes("loca")) {
      return (
        <a
          href={`https://maps.google.com/?q=${getLocationCoordinates(
            field,
            item
          )}`}
          target="_blank"
          rel="noreferrer"
          style={{
            cursor: "pointer",
            textDecoration: "underline",
            color: "inherit",
          }}
          title={value}
        >
          <div>{value}</div>
        </a>
      );
    } else if (field.toLowerCase().includes("address")) {
      return (
        <span title={value} style={{ cursor: "help" }}>{value}</span>
      );
    } else if (field.includes("statusDescription")) {
      return (
        <div style={{ display: "flex", justifyContent: "center" }}>
          {item.statusDescription ? (
            <div
              title={item.statusDescription}
              style={{
                backgroundColor: item.statusColor,
                borderRadius: 4,
                width: 16,
                height: 16,
                cursor: "help",
              }}
            />
          ) : (
            <div
              style={{
                backgroundColor: item.statusColor,
                borderRadius: 4,
                width: 16,
                height: 16,
              }}
            />
          )}
        </div>
      );
    } else if (field == "event") {
      const eventColor =
        item.levelEvent == "1"
          ? "#FF0000"
          : item.levelEvent == "2"
          ? "#FFA500"
          : item.levelEvent == "3" && item.event !== ""
          ? "#6495ED"
          : null;

      if (!eventColor) {
        return null;
      }

      return (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div
            title={item.event}
            style={{
              backgroundColor: eventColor,
              borderRadius: 4,
              width: 16,
              height: 16,
              cursor: "help",
            }}
          />
        </div>
      );
    } else if (field.toLowerCase() === "eventtype") {
      return value ?? item.event ?? "";
    }

    return isSummaryVehiclesGrid ? <MaybeUnidentified value={value} /> : value;
  };

  const renderDataVertical = (value, item, field, fieldsIndex) => {
    if (!field) return;

    if (field.toLowerCase().includes("plate")) {
      const plateValue =
        field === "licensePlate"
          ? item[field] ?? item.LicensePlate ?? item.plate ?? item.vehiclePlate ?? ""
          : value;
      const plateTooltip =
        item.PlateTooltip ||
        item.plateTooltip ||
        item.licensePlateType ||
        item.plateType ||
        plateValue;
      return (
        <tr key={fieldsIndex}>
          <div className="tooltipReport">
            <span title={plateTooltip}>{plateValue} </span>
            <span className="tooltipReportText">{plateTooltip}</span>
          </div>
        </tr>
      );
    } else if (
      field.toLowerCase() == "end" ||
      field.toLowerCase() == "start" ||
      field.toLowerCase().includes("date") ||
      isGeneratedAtField(field)
    ) {
      return <tr key={fieldsIndex}>{value ? toDateTime(value) : ""}</tr>;
    } else if (field.toLowerCase() == "ignition") {
      return (
        <tr>
          <div
            style={{
              width: "20px",
              height: "20px",
              cursor: "help",
            }}
          >
            {item.ignition === "Ligado" ? (
              <div title="Ligado">
                <img
                  src="/images/icons/opened.png"
                  alt="Ligado"
                  style={{ width: "24px", marginLeft: "0px" }}
                />
              </div>
            ) : item.ignition === "Desligado" ? (
              <div title="Desligado">
                <img
                  src="/images/icons/closed.png"
                  alt="Desligado"
                  style={{ width: "24px", marginLeft: "0px" }}
                />
              </div>
            ) : (
              <div style={{ cursor: "default" }}>{item.ignition}</div>
            )}
          </div>
        </tr>
      );
    } else if (
      field.toLowerCase().includes("speed") &&
      !field.toLowerCase().includes("count")
    ) {
      if (item.speedStatusDescription && item.speedStatusDescription !== "") {
        return (
          <tr key={fieldsIndex}>
            <div className="tooltipReport">
              <span
                style={{
                  color: `${
                    item.speedStatus == "3" || item.speedStatus == "4"
                      ? "#FFA500"
                      : item.speedStatus != "0"
                      ? "#FF0000"
                      : "inherit"
                  }`,
                }}
              >
                {value ? toSpeedFormat(value) : ""}
              </span>
              <span className="tooltipReportText">
                {item.speedStatusDescription}
              </span>
            </div>
          </tr>
        );
      }
      return <tr key={fieldsIndex}>{value ? toSpeedFormat(value) : ""}</tr>;
    } else if (
      field.toLowerCase().includes("distance") ||
      field.toLowerCase().includes("km")
    ) {
      return <tr key={fieldsIndex}>{value ? toDistanceFormat(value) : ""}</tr>;
    } else if (field.toLowerCase().includes("loca")) {
      return (
        <tr key={fieldsIndex}>
          <a
            href={`https://maps.google.com/?q=${getLocationCoordinates(
              field,
              item
            )}`}
            target="_blank"
            rel="noreferrer"
            style={{
              cursor: "pointer",
              textDecoration: "underline",
              color: "inherit",
            }}
            title={value}
          >
            <div>{value}</div>
          </a>
        </tr>
      );
    } else if (field.toLowerCase().includes("address")) {
      return (
        <tr key={fieldsIndex}>
          <span title={value} style={{ cursor: "help" }}>{value}</span>
        </tr>
      );
    } else if (field.includes("statusDescription")) {
      return (
        <tr key={fieldsIndex}>
          <div style={{ display: "flex", justifyContent: "center" }}>
            {item.statusDescription ? (
              <div
                title={item.statusDescription}
                style={{
                  backgroundColor: item.statusColor,
                  borderRadius: 4,
                  width: 16,
                  height: 16,
                  cursor: "help",
                }}
              />
            ) : (
              <div
                style={{
                  backgroundColor: item.statusColor,
                  borderRadius: 4,
                  width: 16,
                  height: 16,
                }}
              />
            )}
          </div>
        </tr>
      );
    } else if (field == "event") {
      const eventColor =
        item.levelEvent == "1"
          ? "#FF0000"
          : item.levelEvent == "2"
          ? "#FFA500"
          : item.levelEvent == "3" && item.event !== ""
          ? "#6495ED"
          : null;

      if (!eventColor) {
        return <tr key={fieldsIndex} />;
      }

      return (
        <tr key={fieldsIndex}>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div
              title={item.event}
              style={{
                backgroundColor: eventColor,
                borderRadius: 4,
                width: 16,
                height: 16,
                cursor: "help",
              }}
            />
          </div>
        </tr>
      );
    } else if (field.toLowerCase() === "eventtype") {
      return (
        <tr key={fieldsIndex}>
          {value ?? item.event ?? ""}
        </tr>
      );
    }
    return <tr key={fieldsIndex}>{value}</tr>;
  };

  const getData = (data) => {
    if (!data) return [];

    // Se for objeto direto com .items ou .vehiclePositions
    if (!Array.isArray(data) && typeof data === "object") {
      if (Array.isArray(data.items)) return dedupeItems(data.items);
      if (Array.isArray(data.vehiclePositions)) return dedupeItems(data.vehiclePositions);
    }

    // Se for um array de dados
    if (Array.isArray(data)) {
      if (data.length === 0) return [];
      
      // Se for um array envelopando um objeto que contém .items ou .vehiclePositions (ex: /vehiclepositions paginado)
      if (data.length === 1 && data[0] && typeof data[0] === "object") {
        if (Array.isArray(data[0].items)) return dedupeItems(data[0].items);
        if (Array.isArray(data[0].vehiclePositions)) return dedupeItems(data[0].vehiclePositions);
      }
      
      return dedupeItems(data);
    }

    return [];
  };

  const data = normalizeTemporalData(getData(props.gridData), fields);
  const isPageableArray = Array.isArray(data) && data.length > 0 && !data?.error;

  // Para os casos sem GridComponent do Syncfusion (vazio, erro ou objeto
  // único), não existe evento `dataBound` — a montagem é síncrona com o
  // próprio render do React, então avisamos o pai assim que o efeito rodar.
  // O caso com GridComponent avisa via `dataBound`/`actionComplete` abaixo.
  useEffect(() => {
    if (!isPageableArray && props.onGridRendered) {
      props.onGridRendered();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.gridData]);

  // Debounce simples para resize + recálculo ao terminar loading (mesmo
  // padrão de VehiclesNickame.jsx) — reajusta pageSize conforme o espaço
  // disponível na viewport, evitando altura "sobrando" ou grid cortada.
  useEffect(() => {
    const items = isPageableArray ? data : [];
    paginationService.calculateGridMetrics(
      gridAnchorRef,
      BOTTOM_MARGIN,
      HEADER_PAGER_BUDGET,
      ROW_HEIGHT, MIN_PAGE,
      MAX_PAGE,
      items,
      window.innerHeight,
      setPageSize
    );

    let resizeTimeout = null;
    const onResize = () => {
      if (resizeTimeout) window.clearTimeout(resizeTimeout);
      resizeTimeout = window.setTimeout(() => paginationService.calculateGridMetrics(
        gridAnchorRef,
        BOTTOM_MARGIN,
        HEADER_PAGER_BUDGET,
        ROW_HEIGHT, MIN_PAGE,
        MAX_PAGE,
        items,
        window.innerHeight,
        setPageSize
      ), 50);
    };

    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      if (resizeTimeout) window.clearTimeout(resizeTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.gridData, props.isLoading]);

  return (
    <div className="card">
      <div ref={gridAnchorRef} />
      {Array.isArray(data) && data.length === 0 ? (
        <EmptyData />
      ) : data?.error ? (
        <EmptyData />
      ) : !Array.isArray(data) ? (
        // Caso raro: a API devolveu um único objeto em vez de uma lista.
        // Mantido como tabela chave/valor simples (não é uma grade tabular).
        <div className="table-responsive text-nowrap">
          <table className="table table-grid" id="paginated-table">
            <tbody className="table-border-bottom-0">
              {fields.map((field, fieldIndex) => (
                <tr key={fieldIndex}>
                  <td>{columns[fieldIndex]}</td>
                  <td>
                    {renderDataVertical(data[field], data, field, fieldIndex)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <GridComponent
          dataSource={data}
          allowPaging={true}
          allowSorting={true}
          allowFiltering={true}
          filterSettings={{ type: "Menu" }}
          pageSettings={{ pageSize }}
          rowHeight={ROW_HEIGHT}
          height="auto"
          locale="pt-BR"
          cssClass={isSummaryVehiclesGrid ? "summary-vehicles-table" : ""}
          emptyRecordTemplate={() => <EmptyData />}
          dataBound={() => {
            if (props.onGridRendered) {
              props.onGridRendered();
            }
          }}
          actionComplete={() => {
            setTimeout(() => {
              paginationService.calculateGridMetrics(
                gridAnchorRef,
                BOTTOM_MARGIN,
                HEADER_PAGER_BUDGET,
                ROW_HEIGHT,
                MIN_PAGE,
                MAX_PAGE,
                data,
                window.innerHeight,
                setPageSize
              );
              if (props.onGridRendered) {
                props.onGridRendered();
              }
            }, 50);
          }}
        >
          <ColumnsDirective>
            {visibleFieldColumnPairs.map(({ field, column }) => (
              <ColumnDirective
                key={field}
                field={field}
                headerText={column}
                type={isTemporalField(field) ? "dateTime" : undefined}
                width={columnWidthsByGrid[props.pageGrid]?.[field]}
                textAlign={columnAlignmentsByGrid[props.pageGrid]?.[field] || "Left"}
                customAttributes={field.toLowerCase().includes("address") ? { class: "address-col" } : undefined}
                template={(rowData) => (
                  <>{renderCellContent(rowData, field)}</>
                )}
              />
            ))}
          </ColumnsDirective>
          <Inject services={[Page, Filter, Sort]} />
        </GridComponent>
      )}
    </div>
  );
}
